import { AfterContentInit, Component, ElementRef, input, contentChildren, output, inject, effect } from '@angular/core';
import { NgClass } from '@angular/common';
import { CdkPortalOutlet } from '@angular/cdk/portal';

import { ApTabComponent } from './tab/tab.component';

/**
 * @ngdoc component
 * @name ApTabsComponent
 * @module ApTabsModule
 * @description
 * Component for tabs
 * This component holds tab components and selects a tab as active in two ways:
 * 1. The tab it self is set active on the template
 * 2. The title of the tab to be active is given in attribute activeTabTitle in tabs element
 */
@Component({
    selector: 'ap-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.scss'],
    imports: [NgClass, CdkPortalOutlet]
})
export class ApTabsComponent implements AfterContentInit {
    private readonly elementRef = inject(ElementRef);

    public readonly tabs = contentChildren(ApTabComponent);

    /**
     * Active tab id, to programatically select a tab.
     */
    public readonly activeTabId = input<string>(undefined);

    /**
     * Tab alignment.
     * Defaults ot `left`
     */
    public readonly tabAlignment = input<'left' | 'center'>('left');

    public readonly selectionChanged = output<string>();

    public constructor() {
        effect(() => {
            if (!this.tabs()?.length) {
                return;
            }

            this.init();
        });
    }

    public ngAfterContentInit(): void {
        this.init();
    }

    protected onKeydown(event: KeyboardEvent, index: number): void {
        switch (event.key) {
            case 'Left':
            case 'ArrowLeft':
                this.focusActiveTab(index > 0 ? index - 1 : this.getZeroBasedQueryListLength(this.tabs()));
                break;
            case 'Right':
            case 'ArrowRight':
                this.focusActiveTab(index < this.getZeroBasedQueryListLength(this.tabs()) ? index + 1 : 0);
                break;
            case 'Space':
            case 'Enter':
                this.setActiveItem(index);
                break;
            case 'Home':
                this.focusActiveTab(0);
                break;
            case 'End':
                this.focusActiveTab(this.getZeroBasedQueryListLength(this.tabs()));
                break;
            default:
                return;
        }

        event.preventDefault();
    }

    /**
     * Set the active tab by index
     */
    public setActiveItem(index: number): void {
        this.selectTab(index);
    }

    /**
     * @internal
     */
    public selectTab(index: number): void {
        // Deactivate all tabs and activate selected tab so the corresponding content is displayed
        const tabs = this.tabs();

        // Only emit the changed event when a different tab is selected
        if (!tabs[index].active) {
            this.selectionChanged.emit(tabs[index].id());
        }
        tabs.forEach((tab) => (tab.active = false));
        tabs[index].active = true;
    }

    protected isTabAlignmentCentered(): boolean {
        return this.tabAlignment() === 'center';
    }

    private init(): void {
        // Get the active tabs based on title equals activeTabTitle or active property.
        const tabs = this.tabs();
        let activeTab: ApTabComponent = this.tabs().find((tab) => tab.id() === this.activeTabId());

        if (activeTab) {
            this.setActiveItem(tabs.indexOf(activeTab));
        } else {
            activeTab = this.tabs().find((tab) => tab.active);

            if (activeTab) {
                this.setActiveItem(tabs.indexOf(activeTab));
            } else {
                this.setActiveItem(0);
            }
        }
    }

    private getZeroBasedQueryListLength(list: readonly unknown[]): number {
        return list.length - 1;
    }

    private focusActiveTab(index?: number): void {
        const element =
            index != null
                ? this.elementRef.nativeElement.querySelectorAll('button')[index]
                : this.elementRef.nativeElement.querySelector('button.active');

        if (element) {
            element.focus();

            const parent = this.elementRef.nativeElement.querySelector('.tab-holder');
            this.setScrollOffset(element, parent);
        }
    }

    private setScrollOffset(element: HTMLElement, parent: HTMLElement): void {
        if (!parent) {
            return;
        }
        const scrollOffset = 24;
        const parentWidth = parent.offsetWidth;
        const parentOffset = parent.getBoundingClientRect().left;
        const elementPosition = element.getBoundingClientRect();

        // Use scrollLeft and manually calculate the offset required
        // element.scrollIntoView() does not work in combination with an offset
        // and smooth transition behaviour.
        if (Math.round(elementPosition.left) - parentOffset < scrollOffset) {
            parent.scrollLeft -= Math.abs(elementPosition.left) + scrollOffset + parentOffset;
        } else if (elementPosition.right - parentOffset + scrollOffset > parentWidth) {
            parent.scrollLeft += elementPosition.right + scrollOffset - parentWidth - parentOffset;
        }
    }
}
